import React, { useMemo } from "react";
import { motion, AnimatePresence } from "framer-motion";
import {
  Trophy,
  X,
  TrendingUp,
  DollarSign,
  Clock,
  PieChart,
  Award,
  Building2,
  CheckCircle2,
  XCircle,
} from "lucide-react";
import { Opportunity } from "../../types";
import { computeWinLossStats } from "../../services/winLossAnalyticsService";

export interface WinLossAnalyticsModalProps {
  isOpen: boolean;
  opportunities: Opportunity[];
  onClose: () => void;
}

export const WinLossAnalyticsModal: React.FC<WinLossAnalyticsModalProps> = ({
  isOpen,
  opportunities,
  onClose,
}) => {
  const stats = useMemo(() => computeWinLossStats(opportunities), [opportunities]);

  if (!isOpen) return null;

  return (
    <AnimatePresence>
      <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
        {/* Backdrop */}
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          onClick={onClose}
          className="fixed inset-0 bg-slate-900/60 backdrop-blur-xs"
        />

        {/* Modal Window */}
        <motion.div
          initial={{ opacity: 0, scale: 0.95, y: 15 }}
          animate={{ opacity: 1, scale: 1, y: 0 }}
          exit={{ opacity: 0, scale: 0.95, y: 15 }}
          transition={{ duration: 0.2 }}
          className="relative z-10 w-full max-w-4xl max-h-[90vh] flex flex-col rounded-2xl border border-slate-200 bg-white shadow-2xl dark:border-slate-800 dark:bg-slate-900 overflow-hidden"
        >
          {/* Header */}
          <div className="flex items-center justify-between border-b border-slate-200 px-6 py-4 dark:border-slate-800">
            <div className="flex items-center gap-3">
              <div className="flex h-10 w-10 items-center justify-center rounded-xl bg-teal-100 text-[#2ba5b5] dark:bg-teal-950/60 dark:text-teal-300">
                <PieChart className="h-5 w-5" />
              </div>
              <div>
                <h3 className="text-base font-bold text-slate-900 dark:text-white">
                  Win / Loss & Post-Mortem Intelligence
                </h3>
                <p className="text-xs text-slate-500 dark:text-slate-400">
                  Aggregate analytics across {stats.totalDecided} decided opportunities
                </p>
              </div>
            </div>

            <button
              type="button"
              onClick={onClose}
              className="rounded-lg p-1.5 text-slate-400 hover:bg-slate-100 hover:text-slate-600 dark:hover:bg-slate-800 dark:hover:text-slate-200"
            >
              <X className="h-5 w-5" />
            </button>
          </div>

          {/* Body */}
          <div className="flex-1 overflow-y-auto p-6 space-y-6">
            {stats.totalDecided === 0 ? (
              <div className="text-center py-12">
                <Trophy className="h-12 w-12 mx-auto text-slate-300 dark:text-slate-600" />
                <h4 className="mt-3 text-sm font-bold text-slate-800 dark:text-slate-200">
                  No Decided Opportunities Yet
                </h4>
                <p className="mt-1 text-xs text-slate-500 max-w-md mx-auto">
                  When bids conclude, record their outcomes in Stage 7 (Debrief & Learn) to populate
                  win rates, factor frequencies, and competitor intelligence.
                </p>
              </div>
            ) : (
              <>
                {/* KPI Metrics Strip */}
                <div className="grid grid-cols-2 gap-3 sm:grid-cols-4">
                  <div className="rounded-xl border border-slate-200/80 bg-slate-50/50 p-4 dark:border-slate-800 dark:bg-slate-950/40">
                    <div className="flex items-center justify-between text-xs font-semibold text-slate-500">
                      <span>Win Rate</span>
                      <Award className="h-4 w-4 text-[#2ba5b5]" />
                    </div>
                    <div className="mt-2 text-2xl font-black text-slate-900 dark:text-white">
                      {stats.winRate}%
                    </div>
                    <div className="mt-1 text-[11px] text-slate-500">
                      {stats.wonCount} won · {stats.lostCount} lost
                    </div>
                  </div>

                  <div className="rounded-xl border border-slate-200/80 bg-slate-50/50 p-4 dark:border-slate-800 dark:bg-slate-950/40">
                    <div className="flex items-center justify-between text-xs font-semibold text-slate-500">
                      <span>Total Awarded $</span>
                      <DollarSign className="h-4 w-4 text-emerald-500" />
                    </div>
                    <div className="mt-2 text-2xl font-black text-emerald-600">
                      ${stats.totalWonValue.toLocaleString("en-US")}
                    </div>
                    <div className="mt-1 text-[11px] text-slate-500">
                      Across {stats.wonCount} won contracts
                    </div>
                  </div>

                  <div className="rounded-xl border border-slate-200/80 bg-slate-50/50 p-4 dark:border-slate-800 dark:bg-slate-950/40">
                    <div className="flex items-center justify-between text-xs font-semibold text-slate-500">
                      <span>Avg Cycle Time</span>
                      <Clock className="h-4 w-4 text-teal-500" />
                    </div>
                    <div className="mt-2 text-2xl font-black text-slate-900 dark:text-white">
                      {stats.avgCycleTimeDays}
                      <span className="text-sm font-medium text-slate-400 ml-1">days</span>
                    </div>
                    <div className="mt-1 text-[11px] text-slate-500">Intake to award notice</div>
                  </div>

                  <div className="rounded-xl border border-slate-200/80 bg-slate-50/50 p-4 dark:border-slate-800 dark:bg-slate-950/40">
                    <div className="flex items-center justify-between text-xs font-semibold text-slate-500">
                      <span>Total Decided</span>
                      <TrendingUp className="h-4 w-4 text-indigo-500" />
                    </div>
                    <div className="mt-2 text-2xl font-black text-slate-900 dark:text-white">
                      {stats.totalDecided}
                    </div>
                    <div className="mt-1 text-[11px] text-slate-500">
                      {stats.disqualifiedCount} disq · {stats.withdrawnCount} w/d
                    </div>
                  </div>
                </div>

                {/* Service Line Win Rates Table */}
                <div className="rounded-xl border border-slate-200/80 bg-white p-5 shadow-2xs dark:border-slate-800 dark:bg-slate-900">
                  <h4 className="text-sm font-bold text-slate-900 dark:text-white">
                    Win Rates by Service Line
                  </h4>
                  <div className="mt-3 overflow-x-auto">
                    <table className="w-full text-left text-xs">
                      <thead>
                        <tr className="border-b border-slate-200 text-slate-500 dark:border-slate-800">
                          <th className="pb-2 font-semibold">Service Line</th>
                          <th className="pb-2 font-semibold">Won / Lost</th>
                          <th className="pb-2 font-semibold">Total</th>
                          <th className="pb-2 font-semibold text-right">Win Rate</th>
                        </tr>
                      </thead>
                      <tbody className="divide-y divide-slate-100 dark:divide-slate-800">
                        {stats.serviceBreakdown.map((s) => (
                          <tr key={s.service}>
                            <td className="py-2.5 font-medium text-slate-800 dark:text-slate-200">
                              {s.service}
                            </td>
                            <td className="py-2.5 text-slate-600 dark:text-slate-400">
                              <span className="text-emerald-600 font-bold">{s.won}</span> /{" "}
                              <span className="text-rose-600 font-bold">{s.lost}</span>
                            </td>
                            <td className="py-2.5 text-slate-600 dark:text-slate-400">{s.total}</td>
                            <td className="py-2.5 text-right font-bold">
                              <div className="flex items-center justify-end gap-2">
                                <div className="h-1.5 w-16 rounded-full bg-slate-100 overflow-hidden dark:bg-slate-800">
                                  <div
                                    className="h-full bg-[#2ba5b5]"
                                    style={{ width: `${s.winRate}%` }}
                                  />
                                </div>
                                <span>{s.winRate}%</span>
                              </div>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>

                {/* Factors & Competitors Grid */}
                <div className="grid gap-6 md:grid-cols-2">
                  {/* Top Winning & Losing Factors */}
                  <div className="rounded-xl border border-slate-200/80 bg-white p-5 shadow-2xs dark:border-slate-800 dark:bg-slate-900">
                    <h4 className="text-sm font-bold text-slate-900 dark:text-white">
                      Deciding Factor Frequencies
                    </h4>
                    <p className="mt-1 text-xs text-slate-500">
                      Drivers cited in post-mortem debriefs.
                    </p>

                    <div className="mt-4 space-y-2.5">
                      {stats.factorFrequency.slice(0, 6).map((f) => (
                        <div key={f.factor} className="space-y-1">
                          <div className="flex items-center justify-between text-xs">
                            <span className="font-medium text-slate-700 dark:text-slate-300">
                              {f.label}
                            </span>
                            <div className="flex items-center gap-2 text-[11px]">
                              {f.wonCount > 0 && (
                                <span className="inline-flex items-center gap-0.5 text-emerald-600 font-bold">
                                  <CheckCircle2 className="h-3 w-3" />
                                  {f.wonCount} won
                                </span>
                              )}
                              {f.lostCount > 0 && (
                                <span className="inline-flex items-center gap-0.5 text-rose-600 font-bold">
                                  <XCircle className="h-3 w-3" />
                                  {f.lostCount} lost
                                </span>
                              )}
                            </div>
                          </div>
                          <div className="flex h-1.5 w-full rounded-full bg-slate-100 overflow-hidden dark:bg-slate-800">
                            <div
                              className="bg-emerald-500"
                              style={{
                                width: `${(f.wonCount / Math.max(1, f.wonCount + f.lostCount)) * 100}%`,
                              }}
                            />
                            <div
                              className="bg-rose-500"
                              style={{
                                width: `${(f.lostCount / Math.max(1, f.wonCount + f.lostCount)) * 100}%`,
                              }}
                            />
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>

                  {/* Competitor Win Leaderboard */}
                  <div className="rounded-xl border border-slate-200/80 bg-white p-5 shadow-2xs dark:border-slate-800 dark:bg-slate-900">
                    <h4 className="text-sm font-bold text-slate-900 dark:text-white flex items-center gap-2">
                      <Building2 className="h-4 w-4 text-[#2ba5b5]" />
                      <span>Winning Competitors (Lost Bids)</span>
                    </h4>
                    <p className="mt-1 text-xs text-slate-500">
                      Competitors who were awarded contracts over MLC.
                    </p>

                    {stats.topCompetitors.length > 0 ? (
                      <div className="mt-4 space-y-2">
                        {stats.topCompetitors.slice(0, 6).map((c) => (
                          <div
                            key={c.name}
                            className="flex items-center justify-between rounded-xl bg-slate-50 p-2.5 text-xs dark:bg-slate-950"
                          >
                            <span className="font-semibold text-slate-800 dark:text-slate-200">
                              {c.name}
                            </span>
                            <span className="rounded-full bg-rose-100 px-2 py-0.5 text-[11px] font-bold text-rose-800 dark:bg-rose-950 dark:text-rose-300">
                              {c.lossCount} {c.lossCount === 1 ? "bid won" : "bids won"}
                            </span>
                          </div>
                        ))}
                      </div>
                    ) : (
                      <div className="mt-8 text-center text-xs text-slate-400 italic">
                        No competitor wins recorded yet.
                      </div>
                    )}
                  </div>
                </div>
              </>
            )}
          </div>

          {/* Footer */}
          <div className="flex justify-end border-t border-slate-200 bg-slate-50/80 px-6 py-3.5 dark:border-slate-800 dark:bg-slate-900/60">
            <button
              type="button"
              onClick={onClose}
              className="btn-animate rounded-xl bg-slate-900 px-4 py-2 text-xs font-semibold text-white shadow-2xs hover:bg-slate-800 dark:bg-white dark:text-slate-900 dark:hover:bg-slate-100"
            >
              Close Insights
            </button>
          </div>
        </motion.div>
      </div>
    </AnimatePresence>
  );
};
